import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { get } from 'lodash';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Alert, Button } from 'reactstrap';
import RouterPropTypes from 'react-router-prop-types';

import { modelOf } from '../../../prop-types';
import PageTitle from '../../common/PageTitle';
import SEOTitle from '../../head/SEOTitle';
import ContentForState from '../../loader/ContentForState';
import Paths from '../../../types/Paths';
import UnexpectedError from '../../loader/UnexpectedError';
import globalTranslations from '../../../i18n/globalTranslations';
import RouteService from '../../../services/RouteService';
import HelperNavigation from '../../common/HelperNavigation';
import PaymentTokenStore from '../../../store/PaymentTokenStore';
import PaymentTokenList from '../PaymentTokenList';
import { isNotHandledByReactRouter } from '../../../util/url';
import generatePath from '../../../util/generatePath';
import RequestState from '../../../types/RequestState';

@observer
export class MyAccountPaymentTokenList extends Component {
  state = {
    removeError: null,
    requestId: 0
  };

  componentDidMount() {
    const { paymentTokenStore } = this.props;
    if (paymentTokenStore.state === RequestState.NONE) {
      paymentTokenStore.loadTokens();
    }
  }

  getBreadcrumbsPath = () => {
    const { intl, routeService } = this.props;
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.myAccount),
        url: Paths.MyAccount
      },
      {
        text: intl.formatMessage(globalTranslations.paymentTokensTitle),
        url: Paths.MyAccountPaymentTokenList
      }
    ]);
  };

  removeToken = (id) => {
    const { paymentTokenStore, routeService, history } = this.props;
    paymentTokenStore
      .removeToken(id)
      .then(() => {
        paymentTokenStore.loadTokens();
        this.setState({ removeError: null });
        const path = routeService.getPath(
          generatePath(Paths.MyAccountPaymentTokenList)
        );
        history.push(path);
      })
      .catch((error) =>
        this.setState({
          removeError: error,
          requestId: this.state.requestId + 1
        })
      );
  };

  renderTokenList = () => {
    const { paymentTokenStore } = this.props;
    return (
      <PaymentTokenList
        tokens={paymentTokenStore.tokens}
        onRemoveClick={this.removeToken}
      />
    );
  };

  renderError = () => {
    const { removeError, requestId } = this.state;
    const errors = get(removeError, 'response.data.errors');
    return errors ? (
      <Alert
        className="MyAccountPaymentTokenList__errors"
        color="danger"
        key={requestId}
      >
        {Object.values(removeError.response.data.errors).map((message) => (
          <div key={message}>{message}</div>
        ))}
      </Alert>
    ) : (
      <UnexpectedError />
    );
  };

  handleNewToken = () => {
    const { paymentTokenStore } = this.props;

    const notHandledByReactRouter = isNotHandledByReactRouter(
      paymentTokenStore.newTokenUrl
    );
    if (notHandledByReactRouter) {
      document.location = paymentTokenStore.newTokenUrl;
    }
  };

  render() {
    const { paymentTokenStore } = this.props;
    const { removeError } = this.state;

    return (
      <div className="MyAccountPaymentTokenList">
        <SEOTitle title={globalTranslations.paymentTokensTitle} />
        <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
        <PageTitle>
          <FormattedMessage {...globalTranslations.paymentTokensTitle} />
        </PageTitle>
        {paymentTokenStore.newTokenUrl && (
          <div className="MyAccountPaymentTokenList__new-token">
            <Button
              color="primary"
              className="MyAccountPaymentTokenList__tools-add-new-button"
              onClick={this.handleNewToken}
            >
              <FormattedMessage {...globalTranslations.paymentTokensNewTitle} />
            </Button>
          </div>
        )}
        {removeError && this.renderError()}
        <ContentForState
          state={paymentTokenStore.state}
          error={paymentTokenStore.lastError}
          forLoaded={() => this.renderTokenList()}
        />
      </div>
    );
  }
}

MyAccountPaymentTokenList.propTypes = {
  paymentTokenStore: modelOf(PaymentTokenStore).isRequired,
  intl: intlShape.isRequired,
  history: RouterPropTypes.history.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired
};

export default injectIntl(
  inject('paymentTokenStore', 'routeService')(
    withRouter(MyAccountPaymentTokenList)
  )
);
